/** @jsx jsx */

import React, { useContext, useState, useRef, useEffect } from 'react';
import { css, jsx } from '@emotion/core';
import Btn from './CarouselBtn';
import Marker from './CarouselMarker';
import { colors } from 'styles/variables';
import { unlockInteraction } from 'services/unlockInteraction';
import { interactionContext as InteractionContext } from './interactionContext';
import { CarouselContext } from './Carousel';
import isEDGE from 'services/isEDGE';
import { swipeGesture } from './swipeGesture';
import { useSelector } from 'react-redux';
import { State } from 'store/store';

const { lGrey0, lGrey2 } = colors;

enum Direction {
  Prev = 'prev',
  Next = 'next',
}

const CarouselLayout = React.memo(({ children }: any) => {
  const interactionContext = useContext(InteractionContext);
  const carouselContext = useContext(CarouselContext)!;
  const { currentIndex, animate } = carouselContext;

  // const imgHeight = window['innerHeight'] - 70 * 2 || 350;

  const {
    backGround = true,
    carouselColor = '#fff',
    backgroundOpacity = 100,
    border = true,
    borderColor = lGrey2,
    borderRadius = 10,
    carouselTextColor = lGrey0,
    animation = 'natural',
    includeText = true,
    includeSlideIndicators = true,
    prevNextBtnColor = '#494949',
    prevNextBtnHoverColor,
    prevNextArrowColor = '#ffffff',
    fullWidth = false,
    carouselHeight = 350,
    fullHeight = false,
  } = interactionContext.props;

  const { layout } = interactionContext.meta;

  const OneColumn = layout.toLowerCase().includes('one');

  const itemsSeen = useRef(
    unlockInteraction(interactionContext.id, (children as any).length)
  );

  const isOpen = useSelector((state: State) => {
    return state.interactionsOpen.includes(
      interactionContext.props.sId || interactionContext.id
    );
  });

  const checkSwipeIsLive = useRef<number | undefined>(undefined);

  const [imgHeight, setImgHeight] = useState(carouselHeight);
  const debounceSetImgHeight = useRef<any>();

  useEffect(() => {
    if (fullHeight) {
      return;
    }

    setImgHeight(carouselHeight);
  }, [carouselHeight, fullHeight, isOpen]);

  useEffect(() => {
    if (!fullHeight) {
      removeEventListener('resize', onResize);
      return;
    }

    if (fullHeight) {
      addEventListener('resize', onResize);
      onResize();
    } else {
      setImgHeight(350); //
    }

    function onResize() {
      clearTimeout(debounceSetImgHeight.current);
      debounceSetImgHeight.current = setTimeout(() => {
        setImgHeight(window.innerHeight - 70 * 3); //
      }, 300);
    }

    return () => {
      removeEventListener('resize', onResize);
    };
  }, [fullHeight, isOpen]);

  useEffect(() => {
    if (window.dev()) {
      return;
    }
    if (interactionContext.props.lockDown === 0) return;
    itemsSeen.current(currentIndex);
  });

  const overflow = backGround ? 'hidden' : 'visible';

  return (
    <div
      css={css({
        width: '100%',
        padding: `10px ${!OneColumn ? '30px' : 0}`,

        ...(!OneColumn &&
          !fullWidth && {
            '@media (max-width: 1100px)': {
              // if viewport is < 1100px
              width: '50vw',
            },
            //
            // updated to align with creator-ui breakpoint !!!. . .
            '@media (max-width: 767px)': {
              // if viewport is < 768px
              // this is our breakpoint where behaviour should
              // be like we're in single column
              width: 'calc(100vw - 30px)',
            },

            '@media (min-width: 1100px)': {
              // if viewport is > 1100px
              maxWidth: 1100 / 2,
            },
          }),

        ...(!OneColumn &&
          fullWidth && {
            // maxWidth: 1100 / 2,
            // width: '100%',
            maxWidth: 'unset',
          }),
      })}
      onTouchStart={swipeGesture((dir: 'prev' | 'next') => {
        carouselContext[dir === 'next' ? Direction.Next : Direction.Prev]();
      })}
    >
      <div
        css={css({
          position: 'relative',
          width: '100%',
          ...(border && {
            border: !backGround ? 'none' : `1px solid ${borderColor}`,
          }),
          borderRadius: borderRadius,
          boxShadow: !backGround
            ? 'none'
            : '5px 10px 20px 10px rgba(0, 0, 0, 0.05)',
          overflow,
        })}
      >
        {backGround && (
          <div
            css={css({
              position: 'absolute',
              left: 0,
              top: 0,
              width: '100%',
              height: '100%',
              background: carouselColor,
              opacity: backgroundOpacity * 0.01,
            })}
          />
        )}
        <ul
          css={css`
            display: flex;
            width: 100%;
            height: auto;
            margin: 0;
            padding: 0 !important;
            overflow: ${overflow};

            ${isEDGE() &&
              !['OneColumn', 'OneColumnReversed'].includes(layout) &&
              `
              max-width: 490px;
        
            `}
          `}
        >
          {(children as any).map((slide, index) =>
            React.cloneElement(slide, {
              index,
              currentIndex,
              animate,
              imgHeight,
              carouselColor,
              carouselTextColor,
              animation,
              slide,
              includeText,
              includeSlideIndicators,
            })
          )}
        </ul>

        <span className="sr-only">
          Showing slide {currentIndex + 1} of {React.Children.count(children)}:
        </span>

        {includeSlideIndicators && (
          <div
            css={css`
              position: absolute;
              display: flex;
              justify-content: center;
              width: 100%;
              top: ${imgHeight - 25}px;
            `}
          >
            <div
              css={css`
                display: flex;
              `}
            >
              {React.Children.map(children, (_, i) => (
                <Marker
                  key={`marker-${i}`}
                  index={i}
                  current={currentIndex === i}
                  color={prevNextBtnColor}
                />
              ))}
            </div>
          </div>
        )}

        <Btn
          dir={Direction.Prev}
          size={40}
          top={`${imgHeight / 2}px`}
          height={imgHeight}
          enabled={currentIndex > 0}
          color={prevNextBtnColor}
          hoverColor={prevNextBtnHoverColor}
          arrowColor={prevNextArrowColor}
          onClick={carouselContext[Direction.Prev]}
        />
        <Btn
          dir={Direction.Next}
          size={40}
          top={`${imgHeight / 2}px`}
          height={imgHeight}
          enabled={currentIndex < React.Children.count(children) - 1}
          color={prevNextBtnColor}
          hoverColor={prevNextBtnHoverColor}
          arrowColor={prevNextArrowColor}
          onClick={carouselContext[Direction.Next]}
        />
      </div>
    </div>
  );
});

export default CarouselLayout;
